import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";

import { authApi } from "@/api/auth";
import { useAuthStore } from "@/stores/auth";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

export function OtpPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const setTokens = useAuthStore((s) => s.setTokens);

  const sid = params.get("sid") || "";
  const uid = params.get("uid") || "";

  const [code, setCode] = useState("");
  const [serverError, setServerError] = useState<string | null>(null);

  const verify = useMutation({
    mutationFn: () => authApi.verifyOtp(uid, sid, code),
    onSuccess: (data) => {
      setTokens(data.access_token, data.refresh_token);
      navigate("/", { replace: true });
    },
    onError: (err: AxiosError<{ error?: { code?: string } }>) => {
      const apiCode = err.response?.data?.error?.code;
      if (apiCode === "invalid_otp") setServerError(t("otp.errors.invalid_otp"));
      else if (apiCode === "rate_limit_otp")
        setServerError(t("otp.errors.rate_limit_otp"));
      else setServerError(t("otp.errors.session_expired"));
    },
  });

  // Якщо немає sid/uid — повертаємо на логін.
  if (!sid || !uid) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Link to="/login" className="text-brand-blue-500 hover:underline">
          {t("otp.errors.session_expired")} →
        </Link>
      </div>
    );
  }

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length !== 6) return;
    setServerError(null);
    verify.mutate();
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-8 p-4
                    bg-gradient-to-br from-brand-navy-50 via-white to-brand-mint-50">
      <Logo variant="full" height={90} />

      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{t("otp.title")}</CardTitle>
          <CardDescription>{t("otp.subtitle")}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4" noValidate>
            <div className="space-y-2">
              <Label htmlFor="code">{t("otp.code_label")}</Label>
              <Input
                id="code"
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                autoComplete="one-time-code"
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className="text-center text-2xl font-mono tracking-[0.5em]"
                aria-invalid={!!serverError}
              />
            </div>

            {serverError && (
              <div
                role="alert"
                className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
              >
                {serverError}
              </div>
            )}

            <Button
              type="submit"
              size="lg"
              className="w-full"
              disabled={code.length !== 6 || verify.isPending}
            >
              {verify.isPending ? t("otp.submitting") : t("otp.submit")}
            </Button>
          </form>

          <div className="mt-4 text-center">
            <Link to="/login" className="text-sm text-brand-blue-500 hover:underline">
              {t("otp.resend")}
            </Link>
          </div>
        </CardContent>
      </Card>

      <div className="text-xs text-slate-400">work.sphereenergy.solar</div>
    </div>
  );
}
